/**
 * Czysta logika trybu wyświetlania i rozdzielczości desktopowej wersji gry.
 *
 * Moduł NIE zależy od Electron ani DOM — jest importowany zarówno przez proces
 * main Electrona (`electron/main.ts`, bundlowany przez vite/rollup), jak i przez
 * renderer, oraz pokryty testami w Vitest. Wszystkie funkcje są deterministyczne.
 */

export type DisplayMode = 'fullscreen' | 'windowed';

export interface Size {
  width: number;
  height: number;
}

export interface DisplaySettings {
  mode: DisplayMode;
  /** Rozmiar okna używany w trybie okienkowym (i jako rozmiar przywracania). */
  windowed: Size;
}

/**
 * Migawka stanu wysyłana z procesu main do renderera: trwałe ustawienia +
 * kontekst monitora potrzebny UI do zbudowania listy rozdzielczości.
 */
export interface DisplayState extends DisplaySettings {
  /** Obszar roboczy głównego monitora. */
  workArea: Size;
  /** Presety rozdzielczości mieszczące się na monitorze. */
  presets: Size[];
}

/** Minimalny rozmiar okna gry — spójny z `minWidth`/`minHeight` w `BrowserWindow`. */
export const MIN_WINDOW: Size = { width: 1024, height: 640 };

/**
 * Standardowe presety rozdzielczości okienkowej, rosnąco. „Natywny"/maksymalny
 * rozmiar reprezentowany jest osobno przez obszar roboczy monitora i nie wchodzi
 * na tę listę.
 */
export const RESOLUTION_PRESETS: readonly Size[] = [
  { width: 1280, height: 720 },
  { width: 1366, height: 768 },
  { width: 1600, height: 900 },
  { width: 1920, height: 1080 },
];

/** Czy `a` mieści się w `b` (oba wymiary ≤). */
function fitsWithin(a: Size, b: Size): boolean {
  return a.width <= b.width && a.height <= b.height;
}

/**
 * Presety mieszczące się w obszarze roboczym monitora, zachowując porządek
 * rosnący z {@link RESOLUTION_PRESETS}.
 */
export function resolutionPresetsFor(workArea: Size): Size[] {
  return RESOLUTION_PRESETS.filter((p) => fitsWithin(p, workArea));
}

/**
 * Dociska rozmiar okna do obszaru roboczego (od góry) oraz do minimum okna
 * (od dołu). Gdy obszar roboczy jest mniejszy niż minimum, wygrywa obszar
 * roboczy — okno nie może być większe niż ekran.
 */
export function clampWindowSize(size: Size, workArea: Size): Size {
  const width = Math.min(Math.max(size.width, MIN_WINDOW.width), workArea.width);
  const height = Math.min(Math.max(size.height, MIN_WINDOW.height), workArea.height);
  return { width, height };
}

/**
 * Presety rozdzielczości RENDEROWANIA (Phaser `Scale.FIT`), rosnąco. W odróżnieniu
 * od {@link RESOLUTION_PRESETS} (rozmiar okna) sięgają wyżej — aż do 4K — bo render
 * jest skalowany do ekranu, nie ograniczony rozmiarem okna.
 */
export const RENDER_PRESETS: readonly Size[] = [
  { width: 1280, height: 720 },
  { width: 1600, height: 900 },
  { width: 1920, height: 1080 },
  { width: 2560, height: 1440 },
  { width: 3840, height: 2160 },
];

/**
 * Presety renderu mieszczące się w rozdzielczości ekranu (rosnąco). Renderowanie
 * powyżej natywnej rozdzielczości jest marnotrawne, więc je odcinamy.
 */
export function renderPresetsFor(screen: Size): Size[] {
  return RENDER_PRESETS.filter((p) => p.width <= screen.width && p.height <= screen.height);
}

/**
 * Domyślna rozdzielczość renderowania przy pierwszym uruchomieniu = natywna
 * rozdzielczość ekranu (najostrzejszy obraz; gracz może ją obniżyć dla wydajności).
 */
export function defaultRenderResolution(screen: Size): Size {
  return { width: screen.width, height: screen.height };
}

/**
 * Domyślny stan wyświetlania przy pierwszym uruchomieniu: tryb pełnoekranowy,
 * a rozmiar okienkowy = największy preset mieszczący się w obszarze roboczym.
 * Gdy żaden preset się nie mieści, używamy obszaru roboczego dociśniętego do
 * dozwolonego zakresu.
 */
export function defaultDisplayFor(workArea: Size): DisplaySettings {
  const fitting = resolutionPresetsFor(workArea);
  const largest = fitting.length > 0 ? fitting[fitting.length - 1] : workArea;
  return {
    mode: 'fullscreen',
    windowed: clampWindowSize(largest, workArea),
  };
}
